import { OrientedBoundingBox as OrientedBoundingBoxTuples } from "@/components/r3f/utils/oriented-bounding-box";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { memberWithPrivateData, OrientedBoundingBox } from "@faro-lotv/lotv";
import { useState } from "react";
import { Matrix4, Plane, Quaternion, Vector3 } from "three";

export type ClippingPlanesReturn = {
  /** The stored clipping planes */
  clippingPlanes: Plane[];
  /** The function to modify the clipping planes */
  setClippingPlanes(p: Plane[]): void;
};

/**
 *
 * @returns an interface to read and write an array of clipping planes to a storage in memory.
 */
export function useClippingPlanes(): ClippingPlanesReturn {
  const clippingBox = useAppSelector(selectClippingBox);

  const [clippingPlanes, setClippingPlanes] = useState(() =>
    obbTuplesToPlanes(clippingBox),
  );

  return {
    clippingPlanes,
    setClippingPlanes,
  };
}

/**
 * @returns the clipping planes for an OrientedBoundingBox
 * @param box the OrientedBoundingBox in tuple form to get the clipping planes for
 */
function obbTuplesToPlanes(box?: OrientedBoundingBoxTuples): Plane[] {
  if (!box) {
    return [];
  }

  return obbToPlanes({
    position: new Vector3().fromArray(box.position),
    quaternion: new Quaternion().fromArray(box.rotation),
    size: new Vector3().fromArray(box.size),
  });
}

/**
 * @returns the clipping planes for an OrientedBoundingBox
 * @param box the OrientedBoundingBox to get the clipping planes for
 */
export function obbToPlanes(box: OrientedBoundingBox): Plane[] {
  // Main direction axes and their distance from the box origin
  const axes: Array<[Vector3, number]> = [
    [new Vector3(1, 0, 0), box.size.x / 2],
    [new Vector3(-1, 0, 0), box.size.x / 2],
    [new Vector3(0, 1, 0), box.size.y / 2],
    [new Vector3(0, -1, 0), box.size.y / 2],
    [new Vector3(0, 0, 1), box.size.z / 2],
    [new Vector3(0, 0, -1), box.size.z / 2],
  ];

  // Compute plane for each main direction
  return axes.map(([axe, distance]) => {
    // The inverted plane normal, going from the box center to the plane
    const invertedNormal = axe.applyQuaternion(box.quaternion);
    // A point on the plane
    const point = box.position
      .clone()
      .add(invertedNormal.clone().multiplyScalar(distance));

    return new Plane().setFromNormalAndCoplanarPoint(
      invertedNormal.negate(),
      point,
    );
  });
}

/**
 * Inverse of {@link obbToPlanes}. Both the clip scene tool and `obbToPlanes` emit the planes as
 * three consecutive (max face, min face) pairs, one pair per box axis.
 *
 * @returns the box delimited by six clipping planes, or undefined if they don't describe a valid box
 * @param planes the six clipping planes, with the normals pointing inside the box
 */
export const planesToObb = memberWithPrivateData(() => {
  const position = new Vector3();
  const quaternion = new Quaternion();
  const size = new Vector3();
  const axes = [new Vector3(), new Vector3(), new Vector3()];

  const cross = new Vector3();

  const rotationMatrix = new Matrix4();

  // The inner function, which can be called normally through the class
  return (planes: Plane[]) => {
    if (planes.length !== 6) return;

    position.set(0, 0, 0);

    for (let i = 0; i < 3; ++i) {
      // Assumes opposite faces of the same axis, as documented in useBoxControlsClippingPlanes
      const max = planes[2 * i];
      const min = planes[2 * i + 1];

      // Since Plane.constant in three.js is defined so that normal·point + constant,
      // the halfSize can be derived knowing that:
      // - pmax = c - d * n_max
      // - pmin = c - d * n_min
      // with c (center of the box), d (the half size of the box) and n_max = -n_min (normal of the planes).
      // By knowing that
      // - pmax * n_max + K_max = 0;
      // - pmin * n_min + K_min = 0;
      // it's easy to derive that d = (K_max + K_min) / 2
      const halfSize = (max.constant + min.constant) / 2;
      if (halfSize <= 0) return;

      axes[i].copy(min.normal);
      size.setComponent(i, 2 * halfSize);
      position.addScaledVector(axes[i], (max.constant - min.constant) / 2);
    }

    // A left-handed basis can't be expressed as a rotation, and the box is symmetric along its axes
    if (cross.crossVectors(axes[0], axes[1]).dot(axes[2]) < 0) {
      axes[2].negate();
    }

    quaternion.setFromRotationMatrix(
      rotationMatrix.makeBasis(axes[0], axes[1], axes[2]),
    );

    return { position, quaternion, size };
  };
});
